"use client"

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  FAVORITE_SOUNDS_STORAGE_KEY,
  FAVORITES_UPDATED_EVENT,
  parseFavoriteSoundIds,
  serializeFavoriteSoundIds,
  toggleFavoriteSoundId,
} from '@/lib/favorites'
import { readStorage, writeStorage } from '@/lib/safe-storage'

// Reads/writes go through lib/safe-storage: a `typeof window` guard is not
// enough, because Android WebViews with DOM storage disabled expose
// `window.localStorage` as null, and a bare `.getItem()` there throws
// "Cannot read properties of null" (Sentry TESLA-LOCK-SOUND-52/53, TES-1872).
// Favouriting degrades to in-memory-only on those clients instead of throwing
// on every mount and every toggle.
function readFavoriteIdsFromStorage(): string[] {
  return parseFavoriteSoundIds(readStorage(FAVORITE_SOUNDS_STORAGE_KEY))
}

function writeFavoriteIdsToStorage(ids: string[]) {
  if (typeof window === 'undefined') return

  // Only broadcast when the write actually landed. The event makes every
  // mounted hook re-read from storage, so firing it after a failed write would
  // immediately clobber the optimistic in-memory state with an empty read and
  // make the heart bounce back to unfavourited on clients without storage.
  if (writeStorage(FAVORITE_SOUNDS_STORAGE_KEY, serializeFavoriteSoundIds(ids))) {
    window.dispatchEvent(new CustomEvent(FAVORITES_UPDATED_EVENT))
  }
}

export function useFavoriteSounds() {
  // null = "not yet hydrated" (SSR + initial client render both start here).
  // string[] = "post-hydration, may be empty". This single initial value
  // prevents the SSR/client render mismatch that caused React hydration #418
  // on /sounds/[id] when a user had favorites stored in localStorage.
  const [favoriteIds, setFavoriteIds] = useState<string[] | null>(null)
  const hasHydrated = favoriteIds !== null

  useEffect(() => {
    const syncFavorites = () => {
      setFavoriteIds(readFavoriteIdsFromStorage())
    }

    // Initial load from localStorage — called via function to satisfy the
    // react-hooks/set-state-in-effect rule (setState inside callback, not body).
    // eslint-disable-next-line react-doctor/no-initialize-state -- post-hydration localStorage sync is intentional
    syncFavorites()

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key === FAVORITE_SOUNDS_STORAGE_KEY) {
        syncFavorites()
      }
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener(FAVORITES_UPDATED_EVENT, syncFavorites)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener(FAVORITES_UPDATED_EVENT, syncFavorites)
    }
  }, [])

  const ids = useMemo(() => favoriteIds ?? [], [favoriteIds])

  const isFavorite = useCallback(
    (soundId: string) => ids.includes(soundId),
    [ids]
  )

  const toggleFavorite = useCallback(
    (soundId: string) => {
      const result = toggleFavoriteSoundId(ids, soundId)
      setFavoriteIds(result.ids)
      writeFavoriteIdsToStorage(result.ids)
      return result.isFavorite
    },
    [ids]
  )

  const clearFavorites = useCallback(() => {
    setFavoriteIds([])
    writeFavoriteIdsToStorage([])
  }, [])

  return {
    favoriteIds: ids,
    favoriteCount: ids.length,
    hasHydrated,
    hasFavorites: ids.length > 0,
    isFavorite,
    toggleFavorite,
    clearFavorites,
  }
}

export type UseFavoriteSoundsReturn = ReturnType<typeof useFavoriteSounds>

export function useSortedFavoriteIds() {
  const { favoriteIds } = useFavoriteSounds()

  return useMemo(() => [...favoriteIds], [favoriteIds])
}
